import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, toastFailure, useSearch } from "@/utils";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { InputField } from "@/components/InputField/InputField";

import { LiEventType } from "@/enum/lievent.enum";

import {
	useCreateLiEventMutation,
	useUpdateLiEventMutation,
	useValidateEventUrlMutation,
} from "@/pages/Private/redux/liEvent/liEvent.api";

import { Spinner } from "@/components/Spinner/Spinner";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { classNames } from "@/utils/classNames";

import {
	LinkedinOrganizationSuggestion,
	LinkedinOrganizationSuggestionItem,
} from "@/components/LinkedinOrganizationSuggestion/LinkedinOrganizationSuggestion";

import { LinkedinPersonSuggestionItem } from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";

import { useLazyGetCompanySuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";

import { useGetProfilesQuery } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { List } from "../../List/schema/list";
import { resolveSourceMode } from "../../List/pages/ListCreate/utils/sourceMode";

import {
	LinkedinParams,
	LinkedinTagAutocomplete,
} from "../../LiPeople/components/LinkedinTagAutocomplete";
import {
	CreateLiEvent,
	CreateLiEventSchema,
	LiEvent,
	ValidateEventUrlResponse,
} from "../schema/liEvent";
import { EventUrlPreviewCard } from "./EventUrlPreviewCard";

interface CreateLiEventFormProps {
	liEvent?: LiEvent | null;
	list: List;
	handleSelect?: (job: LiEvent | null) => void;
	handleDuplicate?: (job: LiEvent | null) => void;
}

export const CreateLiEventForm: FunctionComponent<CreateLiEventFormProps> = ({
	liEvent,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liEvent.${key}`);
	const [createLiEvent, { isLoading, error }] = useCreateLiEventMutation();
	const [updateLiEvent, updateState] = useUpdateLiEventMutation();
	const [validateEventUrl, { isLoading: isValidating }] = useValidateEventUrlMutation();
	const [validationResult, setValidationResult] = useState<ValidateEventUrlResponse | null>(null);
	const [validationError, setValidationError] = useState<string | null>(null);

	const { query: profileQuery, setSearchValue: setProfileSearchValue } = useSearch();

	const { data: profiles } = useGetProfilesQuery({
		page: 1,
		limit: 20,
		searchValue: profileQuery,
	});

	const [organizationSuggestions, setOrganizationSuggestions] =
		useState<LinkedinPersonSuggestionItem[]>();
	const { query, setSearchValue } = useSearch();

	const [fetchCompanies] = useLazyGetCompanySuggestionQuery();

	useEffect(() => {
		if (query) {
			fetchCompanies({ keyword: query }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setOrganizationSuggestions([...optionValues]);
				}
			});
		}
	}, [fetchCompanies, query]);

	const initialFormState = {
		name: "",
		type: resolveSourceMode(Object.values(LiEventType), LiEventType.SPECIFIC),
		eventUrls: [],
		searchTermInTitle: false,
		locationList: [],
		industryList: [],
		titleKeyword: "",
		firstConnection: false,
		secondConnection: false,
		thirdConnection: false,
		now: false,
		atEventDate: false,
		xDaysBeforeEvent: null,
		alsoPastEvents: false,
		fromOrganization: null,
		plannedScrapingUserId: null,
		plannedScrapingUserName: "",
	};

	const {
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
		watch,
	} = useForm<CreateLiEvent>({
		defaultValues: initialFormState,
		resolver: zodResolver(CreateLiEventSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (liEvent) {
			const newArr: CreateLiEvent = {
				id: liEvent.id,
				name: liEvent.name,
				type: liEvent.type,
				searchTerm: liEvent.searchTerm ?? "",
				searchTermInTitle: liEvent.searchTermInTitle,
				locationList: liEvent.locationList || [],
				industryList: liEvent.industryList || [],
				titleKeyword: liEvent.titleKeyword,
				firstConnection: liEvent.firstConnection,
				secondConnection: liEvent.secondConnection,
				thirdConnection: liEvent.thirdConnection,
				now: liEvent.now,
				atEventDate: liEvent.atEventDate,
				xDaysBeforeEvent: liEvent.xDaysBeforeEvent,
				alsoPastEvents: liEvent.alsoPastEvents,
				fromOrganization: liEvent.fromOrganization,
				eventUrls: liEvent.liEventEntries?.map((liEventEntry) => liEventEntry.url),
				plannedScrapingUserId: liEvent.plannedScrapingUserId,
			};

			reset({
				...newArr,
			});
		} else {
			reset(initialFormState);
		}
	}, [list.scrapeLevel, liEvent, reset, handleDuplicate]);

	const onSubmit = async (values: CreateLiEvent) => {
		if (values.type === LiEventType.SEARCH || values.type === LiEventType.COMPANY) {
			values.eventUrls = [];
		} else {
			values.searchTerm = "";
		}

		try {
			if (liEvent?.id && !handleDuplicate) {
				await updateLiEvent({ id: liEvent.id, ...values, listId: list.id }).unwrap();
			} else {
				await createLiEvent({ ...values, listId: list.id }).unwrap();
			}

			reset({});
			setValidationResult(null);
			setValidationError(null);

			handleSelect?.(null);
			handleDuplicate?.(null);
		} catch (err) {
			toastFailure(err, t("errors.saveFailed"));
		}
	};

	const handleValidateUrl = async () => {
		const url = formValues.eventUrls?.[0];

		if (!url) {
			return;
		}

		setValidationError(null);
		setValidationResult(null);

		try {
			const result = await validateEventUrl({ url }).unwrap();

			setValidationResult(result);
		} catch {
			setValidationError(ts("validationFailed"));
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];
	const buttonTitle =
		handleDuplicate && liEvent?.id
			? t("basics.copyCriteria")
			: liEvent?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row w-full mb-1">
				<InputField
					error={!!errors.name?.message}
					handleChange={(e: ChangeEvent<HTMLInputElement>) => {
						setValue("name", e.target.value);
					}}
					label={ts("name")}
					name="name"
					placeholder={ts("name")}
					value={formValues.name || ""}
				/>
			</div>

			<div className="flex flex-row w-full mb-1">
				{[LiEventType.SEARCH, LiEventType.COMPANY].includes(formValues.type) ? (
					<InputField
						error={!!errors.searchTerm?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("searchTerm", e.target.value);
						}}
						label={ts("searchTerm")}
						name="searchTerm"
						placeholder={ts("searchTerm")}
						value={formValues.searchTerm || ""}
					/>
				) : (
					<div className="flex flex-col w-full">
						<div className="flex flex-row gap-2 items-end w-full">
							<div className="flex-1">
								<InputField
									className="-mb-3"
									error={!!errors.eventUrls?.message}
									handleBlur={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
										const urls = e.target.value?.split(",") || [];

										setValue("eventUrls", urls);
										setValidationResult(null);
										setValidationError(null);
									}}
									handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
										setValue("eventUrls", e.target.value?.split(",") || []);
									}}
									label={ts("url")}
									name="eventUrls"
									placeholder={ts("urls")}
									showError={!!errors.eventUrls?.message}
									value={formValues.eventUrls?.join(",") || ""}
								/>
							</div>
							<div className="">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									disabled={
										!formValues.eventUrls?.[0] ||
										!formValues.eventUrls[0].includes("linkedin.com/events/")
									}
									isLoading={isValidating}
									size={ButtonSize.L}
									title={ts("validateUrl")}
									onClick={handleValidateUrl}
								/>
							</div>
						</div>
						{validationError && (
							<div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200">
								<p className="text-sm text-red-700">{validationError}</p>
							</div>
						)}
						{isValidating && (
							<div className="flex items-center gap-2 mt-2 text-text-meta text-sm">
								<Spinner color="#4d66f5" />
								<span>{ts("validating")}</span>
							</div>
						)}
						{validationResult && !isValidating && (
							<EventUrlPreviewCard validationResult={validationResult} />
						)}
					</div>
				)}
			</div>
			<div className="flex flex-col w-full mb-1">
				{formValues.type === LiEventType.COMPANY ? (
					<>
						<label className="text-sm mb-2">{ts("fromOrganization")}</label>
						<LinkedinOrganizationSuggestion
							data={organizationSuggestions || []}
							floating={true}
							handleSearch={(val) => setSearchValue(val)}
							handleSelect={function (
								value?: LinkedinOrganizationSuggestionItem | undefined
							): void {
								if (value) {
									setValue("fromOrganization", value);
									setOrganizationSuggestions([]);
									setSearchValue("");
								} else {
									setValue("fromOrganization", null);
								}
							}}
							selectedItem={
								(formValues.fromOrganization as LinkedinOrganizationSuggestionItem) || undefined
							}
						/>
					</>
				) : (
					<></>
				)}
			</div>
			<div className="flex flex-row w-full mb-1">
				<AutoComplete
					data={
						profiles?.data?.map((c) => {
							return {
								id: c.id,
								title: c.firstName + " " + c.lastName,
							} as AutoCompleteItem;
						}) || []
					}
					handleSearch={(value: string) => setProfileSearchValue(value)}
					handleSelect={async (item?: AutoCompleteItem) => {
						await setValue("plannedScrapingUserId", item?.id as number | null);
					}}
					information="Select a user to scrape attendees of events. Leave empty for random profile. The user will automatically sign up and sign out of the events. The user needs to be logged in with linkedin."
					label={"Select scraping signalist user"}
					placeholder="Leave empty for random profile"
					selectedItem={
						formValues.plannedScrapingUserId
							? profiles?.data.find((p) => p.id === formValues.plannedScrapingUserId)?.firstName +
								" " +
								profiles?.data.find((p) => p.id === formValues.plannedScrapingUserId)?.lastName
							: ""
					}
				/>
			</div>
			<div className="text-smd mb-5 mt-10">When to scrape attendees of events</div>
			<div className="flex flex-col gap-4 w-full mb-4">
				<Checkbox
					error={!!errors.now?.message}
					isChecked={formValues.now}
					showError={!!errors.now?.message}
					onChange={() => setValue("now", !formValues.now)}
				>
					Now
				</Checkbox>

				<Checkbox
					error={!!errors.xDaysBeforeEvent?.message}
					isChecked={formValues.xDaysBeforeEvent !== null}
					onChange={() => {
						setValue("xDaysBeforeEvent", formValues.xDaysBeforeEvent !== null ? null : 0);
					}}
				>
					X Days Before Event
				</Checkbox>
				{formValues.xDaysBeforeEvent !== null && (
					<InputField
						error={!!errors.xDaysBeforeEvent?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("xDaysBeforeEvent", +e.target.value);
						}}
						label="X Days Before Event"
						name="xDaysBeforeEvent"
						placeholder="Enter number of days"
						showError={!!errors.xDaysBeforeEvent?.message}
						type="number"
						value={formValues.xDaysBeforeEvent?.toString() || ""}
					/>
				)}
				<Checkbox
					error={!!errors.atEventDate?.message}
					isChecked={formValues.atEventDate}
					showError={!!errors.atEventDate?.message}
					onChange={() => {
						setValue("atEventDate", !formValues.atEventDate);
					}}
				>
					At Event Date
				</Checkbox>
			</div>

			<div className="text-smd mb-5 mt-10">Only scrape participants with certain criteria</div>

			<div className="flex flex-col w-full mb-6">
				<div className="text-sm font-medium text-gray-900 mb-2">Your Linkedin connection</div>
				<div className="flex flex-row gap-4">
					<div className="flex flex-row">
						<div
							className={classNames(
								"cursor-pointer border border-gray-200 border-r-0 text-placeholder rounded-l-xl px-3 py-3",
								formValues.firstConnection ? "bg-brand-light text-white" : "bg-white"
							)}
							onClick={() => setValue("firstConnection", !formValues.firstConnection)}
						>
							1st
						</div>
						<div
							className={classNames(
								"cursor-pointer border border-gray-200 border-r-0 text-placeholder px-2 py-3",
								formValues.secondConnection ? "bg-brand-light text-white" : "bg-white"
							)}
							onClick={() => setValue("secondConnection", !formValues.secondConnection)}
						>
							2nd
						</div>
						<div
							className={classNames(
								"cursor-pointer border border-gray-200 text-placeholder rounded-r-xl px-3 py-3",
								formValues.thirdConnection ? "bg-brand-light text-white" : "bg-white"
							)}
							onClick={() => setValue("thirdConnection", !formValues.thirdConnection)}
						>
							3rd
						</div>
					</div>
					<div
						className={classNames(
							"cursor-pointer border border-gray-200 text-placeholder rounded-xl px-6 py-3",
							formValues.firstConnection &&
								formValues.secondConnection &&
								formValues.thirdConnection
								? "bg-brand-light text-white"
								: "bg-white"
						)}
						onClick={() => {
							const allActive =
								formValues.firstConnection &&
								formValues.secondConnection &&
								formValues.thirdConnection;

							if (allActive) {
								setValue("firstConnection", false);
								setValue("secondConnection", false);
								setValue("thirdConnection", false);
							} else {
								setValue("firstConnection", true);
								setValue("secondConnection", true);
								setValue("thirdConnection", true);
							}
						}}
					>
						All
					</div>
				</div>
			</div>

			<div className="flex flex-row w-full mb-3">
				<LinkedinTagAutocomplete
					handleValueChange={(value: LinkedinParams[]) => {
						setValue(
							"locationList",
							value?.map((val) => {
								return { title: val.name, urn: val.id };
							})
						);
					}}
					labelText="Personal Linkedin location"
					placeholder="Search for a location"
					type={"location"}
					value={
						formValues.locationList?.map((val) => {
							return { name: val.title, id: val.urn };
						}) || []
					}
				/>
			</div>

			<div className="flex flex-row w-full mb-3">
				<LinkedinTagAutocomplete
					handleValueChange={(value: LinkedinParams[]) => {
						setValue(
							"industryList",
							value?.map((val) => {
								return { title: val.name, urn: val.id };
							})
						);
					}}
					labelText="Personal Linkedin industry"
					placeholder="Search for an industry"
					type={"industry"}
					value={
						formValues.industryList?.map((val) => {
							return { name: val.title, id: val.urn };
						}) || []
					}
				/>
			</div>

			<div className="flex flex-row w-full mb-1">
				<InputField
					error={!!errors.titleKeyword?.message}
					handleChange={(e: ChangeEvent<HTMLInputElement>) => {
						setValue("titleKeyword", e.target.value);
					}}
					label={ts("titleKeyword")}
					name="titleKeyword"
					placeholder={ts("titleKeyword")}
					value={formValues.titleKeyword || ""}
				/>
			</div>

			{getAllErrors(error || updateState.error, formErrors).length
				? renderErrorMessages(getAllErrors(error || updateState.error, formErrors))
				: null}
			<div className="flex gap-4 w-full justify-end">
				{handleSelect && (
					<div className="flex max-w-[100px] w-full">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								handleSelect(null);
								handleDuplicate?.(null);
								reset(initialFormState);
							}}
						/>
					</div>
				)}

				<div className="flex max-w-[150px] w-full">
					<Button
						disabled={false}
						isLoading={isLoading || updateState.isLoading}
						title={buttonTitle}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
